// M24 — remappable keybindings (UI-6). Pure client config (ENG-1: no DOM): a
// registry of bindable actions with their default KeyboardEvent.code, a reverse
// lookup, and MC-style conflict resolution (rebinding a key clears whoever else
// held it). input.ts honors these each keydown; the options screen edits them;
// settings.ts persists them. Movement, jump, sneak/sprint, the nine hotbar slots,
// and the two menu keys are all covered; the mouse buttons stay fixed (M24-remainder).

export type HotbarAction =
  | 'hotbar1' | 'hotbar2' | 'hotbar3' | 'hotbar4' | 'hotbar5'
  | 'hotbar6' | 'hotbar7' | 'hotbar8' | 'hotbar9';

export type GameAction =
  | 'forward' | 'back' | 'left' | 'right' | 'jump' | 'sneak' | 'sprint'
  | 'inventory' | 'advancements' | 'lesson' | 'handRaise' | HotbarAction;

export type Binds = Record<GameAction, string>;

export interface BindMeta {
  readonly action: GameAction;
  readonly label: string;
  readonly default: string;
}

/** Display order + labels + default codes for every bindable action. */
export const BINDABLE: readonly BindMeta[] = [
  { action: 'forward', label: 'Walk Forward', default: 'KeyW' },
  { action: 'back', label: 'Walk Backward', default: 'KeyS' },
  { action: 'left', label: 'Strafe Left', default: 'KeyA' },
  { action: 'right', label: 'Strafe Right', default: 'KeyD' },
  { action: 'jump', label: 'Jump', default: 'Space' },
  { action: 'sneak', label: 'Sneak', default: 'ShiftLeft' },
  { action: 'sprint', label: 'Sprint', default: 'ControlLeft' },
  { action: 'inventory', label: 'Open Inventory', default: 'KeyE' },
  { action: 'advancements', label: 'Advancements', default: 'KeyL' },
  // §29.3/§29.8 (D-TF-186): `KeyL` stays `advancements` — never rebound for these.
  { action: 'lesson', label: 'Lesson', default: 'KeyJ' },
  { action: 'handRaise', label: 'Raise Hand', default: 'KeyH' },
  { action: 'hotbar1', label: 'Hotbar Slot 1', default: 'Digit1' },
  { action: 'hotbar2', label: 'Hotbar Slot 2', default: 'Digit2' },
  { action: 'hotbar3', label: 'Hotbar Slot 3', default: 'Digit3' },
  { action: 'hotbar4', label: 'Hotbar Slot 4', default: 'Digit4' },
  { action: 'hotbar5', label: 'Hotbar Slot 5', default: 'Digit5' },
  { action: 'hotbar6', label: 'Hotbar Slot 6', default: 'Digit6' },
  { action: 'hotbar7', label: 'Hotbar Slot 7', default: 'Digit7' },
  { action: 'hotbar8', label: 'Hotbar Slot 8', default: 'Digit8' },
  { action: 'hotbar9', label: 'Hotbar Slot 9', default: 'Digit9' },
];

/** The hotbar slot index (0..8) an action selects, or -1 if it isn't a hotbar key. */
export function hotbarSlot(action: GameAction | null): number {
  return action && action.startsWith('hotbar') ? Number(action.slice(6)) - 1 : -1;
}

export const DEFAULT_BINDS: Binds = Object.fromEntries(BINDABLE.map((b) => [b.action, b.default])) as Binds;

/** The action bound to a key code, or null when nothing is bound to it. */
export function actionForCode(binds: Binds, code: string): GameAction | null {
  if (!code) return null;
  for (const b of BINDABLE) if (binds[b.action] === code) return b.action;
  return null;
}

/**
 * Bind `action` to `code`. If another action already holds `code`, that one is
 * unbound (set to '') so no key ever drives two actions — Minecraft's conflict
 * behaviour. Returns a NEW binds object (callers assign it to settings.keybinds).
 */
export function rebind(binds: Binds, action: GameAction, code: string): Binds {
  const next: Binds = { ...binds };
  for (const b of BINDABLE) if (b.action !== action && next[b.action] === code) next[b.action] = '';
  next[action] = code;
  return next;
}

/** Merge a saved partial (older versions / missing keys) over the defaults. */
export function normalizeBinds(partial?: Partial<Binds> | null): Binds {
  const out: Binds = { ...DEFAULT_BINDS };
  if (partial) {
    for (const b of BINDABLE) {
      const v = partial[b.action];
      if (typeof v === 'string') out[b.action] = v;
    }
  }
  return out;
}

/** A fresh copy of the defaults — the options screen's Reset button assigns this. */
export function resetBinds(): Binds {
  return { ...DEFAULT_BINDS };
}

const MODIFIER_LABELS: Record<string, string> = {
  ShiftLeft: 'Left Shift', ShiftRight: 'Right Shift',
  ControlLeft: 'Left Ctrl', ControlRight: 'Right Ctrl',
  AltLeft: 'Left Alt', AltRight: 'Right Alt',
};

/** Human label for a key code: 'KeyW'→'W', 'Space'→'Space', 'ArrowUp'→'Up',
 *  'ShiftLeft'→'Left Shift', ''→'—'. */
export function keyLabel(code: string): string {
  if (!code) return '—';
  if (MODIFIER_LABELS[code]) return MODIFIER_LABELS[code];
  if (code === 'Space') return 'Space';
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Arrow')) return code.slice(5);
  if (code.startsWith('Numpad')) return `Num ${code.slice(6)}`;
  return code;
}
